import React, { useEffect, useMemo, useState } from 'react';

// Libraries/Packages
import { Elements } from '@stripe/react-stripe-js';

// Hooks
import { useSelector } from 'react-redux';

// Actions
import { getClientSecret } from '~/redux/actions/stripe-actions';
import { loadStripe } from '@stripe/stripe-js';

// Utils
import { ROLES } from '~/utils/constants';

const appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#ed5c2f',
    colorBackground: '#ffffff',
    colorText: '#30313d',
    colorDanger: '#df1b41',
    fontFamily: 'Inter, sans-serif',
    spacingUnit: '3px',
    borderRadius: '8px',
    iconHoverColor: '#ed5c2f',
  },
  rules: {
    '.Input': {
      border: '1px solid #e5e7eb',
      boxShadow: 'none',
      padding: '12px 16px',
    },
    '.Input:focus': {
      border: '1px solid #ed5c2f',
      boxShadow: 'none',
      outline: 'none',
    },
    '.Input--invalid': {
      border: '1px solid #df1b41',
      boxShadow: 'none',
    },
    '.Label': {
      color: '#111827',
      fontSize: '14px',
      fontWeight: '500',
    },
    '.Tab': {
      border: '1px solid #e5e7eb',
      boxShadow: 'none',
    },
    '.Tab--selected': {
      borderColor: '#ed5c2f',
      boxShadow: 'none',
    },
    '.Tab:focus': {
      boxShadow: 'none',
      outline: 'none',
    },
  },
};

const SETUP_INTENT_TIMEOUT_MS = 15000;

const Stripe = ({
  children,
  enabled = true,
  loadingFallback = null,
  errorFallback = null,
  onError,
}) => {
  const stripePromise = useMemo(() => loadStripe(import.meta.env.VITE_APP_STRIPE_API_KEY), []);
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [setupIntentError, setSetupIntentError] = useState(null);

  const user = useSelector((state) => state.userReducers?.user);
  const hasUser = user && !Array.isArray(user) && Object.keys(user).length > 0;
  const isAdmin = user?.isOrgAdmin || user?.role?.slug === ROLES.ORGANIZATION_ADMIN || false;

  useEffect(() => {
    let isMounted = true;

    if (!enabled) {
      return () => {
        isMounted = false;
      };
    }

    if (hasUser && !isAdmin) {
      setClientSecret(null);
      setIsLoading(false);
      setSetupIntentError(null);
      return () => {
        isMounted = false;
      };
    }

    if (!isAdmin || clientSecret) {
      return () => {
        isMounted = false;
      };
    }

    setIsLoading(true);
    setSetupIntentError(null);

    let timeoutId;
    const setupIntentRequest = getClientSecret();
    const setupIntentTimeout = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error('Payment form initialization timed out.'));
      }, SETUP_INTENT_TIMEOUT_MS);
    });

    Promise.race([setupIntentRequest, setupIntentTimeout])
      .then((res) => {
        const secret = res?.data?.data?.intent?.client_secret;

        if (!secret) {
          throw new Error('Unable to initialize the payment form.');
        }

        if (isMounted) {
          setClientSecret(secret);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setClientSecret(null);
          setSetupIntentError(error);
          if (typeof onError === 'function') {
            onError(error);
          }
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);

        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [clientSecret, enabled, hasUser, isAdmin, onError]);

  const options = {
    clientSecret,
    appearance,
    paymentMethodCreation: 'manual',
  };

  return (
    <div className="flex container mt-8">
      {setupIntentError
        ? errorFallback
        : !clientSecret && isLoading
          ? loadingFallback
          : clientSecret && (
              <Elements stripe={stripePromise} options={options}>
                {children}
              </Elements>
            )}
    </div>
  );
};

export default Stripe;
